import { apiGet, apiSend } from '../../session/client';

/**
 * Everything the Rewards page reads and writes, and nothing it invents.
 *
 * Each call is an existing PENNY route — the ones the Android wallet already
 * uses:
 *
 * - `consumer/penny` — the signed-in person's wallet: balances, the whole
 *   ledger history, their referral record, and the programme in force.
 * - `consumer/penny/referral` — their own referral code (made on first ask, so
 *   the page only asks when the person wants it, never on load).
 * - `consumer/penny/referral/claim` — say who invited you.
 * - `consumer/penny/vouchers/redeem` — a code that grants PENNY.
 * - `consumer/penny-public/offers` — live PENNY campaigns.
 *
 * There is no person id in any path or body: the server decides whose wallet it is.
 * Account credit, voucher stamp cards and the PENNY→credit swap have no domain
 * on the platform (owner decision D8 is open), so nothing here reaches for them.
 */

export type EarnRule =
  | { readonly method: 'PER_POUND'; readonly unitsPerPound: number }
  | { readonly method: 'PERCENT_OF_SPEND'; readonly percentBps: number };

export interface Programme {
  /** The programme's own name, as Just Treated wrote it (e.g. "… (TEST terms — not approved)"). */
  readonly name?: string;
  readonly versionNumber: number;
  readonly unit: string;
  readonly valueMinorPerUnit: number;
  readonly currency: string;
  readonly earn: EarnRule;
  readonly qualifyOn: 'ORDER_CONFIRMED' | 'ORDER_COMPLETED';
  readonly holdDays: number;
  readonly expiryDays: number;
  readonly redemption: {
    readonly minUnits: number;
    readonly maxPercentOfEligibleBps: number;
    readonly minPayableMinor: number;
  };
  readonly bookingCompletedUnits: number;
  readonly regulatedMedicines: string;
}

export interface HistoryLine {
  readonly id: string;
  readonly at: string;
  readonly type: string;
  readonly category: string;
  readonly units: number;
  readonly valueMinor: number;
  readonly currency: string;
  readonly direction: 'IN' | 'OUT' | 'STATE';
  readonly orderId?: string;
  readonly bookingId?: string;
  readonly reason?: string;
  readonly description: string;
}

export interface Wallet {
  readonly status: string;
  readonly unit: string;
  readonly pendingUnits: number;
  readonly availableUnits: number;
  readonly reservedUnits: number;
  readonly spendableUnits: number;
  readonly lifetimeEarnedUnits: number;
  readonly lifetimeRedeemedUnits: number;
  readonly expiringSoon: readonly { readonly units: number; readonly expiresAt: string }[];
  readonly valueMinorPerUnit: number | null;
  readonly currency: string | null;
  readonly history: readonly HistoryLine[];
  readonly referral: {
    readonly code: string | null;
    readonly referred: number;
    readonly qualified: number;
    readonly terms: {
      readonly referrerUnits: number;
      readonly referredUnits: number;
      readonly minQualifyingSpendMinor: number;
    } | null;
  };
  readonly programme: Programme | null;
}

export interface Campaign {
  readonly id: string;
  readonly name: string;
  readonly scope: string;
  readonly funding: string;
  readonly sponsorName?: string;
  readonly kind: 'MULTIPLIER' | 'BONUS_UNITS';
  readonly multiplier?: number;
  readonly bonusUnits?: number;
  readonly endsAt: string;
  readonly newCustomersOnly: boolean;
}

export const getWallet = (): Promise<Wallet> => apiGet<Wallet>('/consumer/penny');

export const getReferralCode = (): Promise<{ readonly code: string }> =>
  apiGet<{ code: string }>('/consumer/penny/referral');

export const claimReferral = (
  code: string,
): Promise<{ readonly status: string; readonly claimedAt: string }> =>
  apiSend('POST', '/consumer/penny/referral/claim', { code });

export const redeemVoucher = (code: string): Promise<{ readonly units: number }> =>
  apiSend('POST', '/consumer/penny/vouchers/redeem', { code });

export const getOffers = async (): Promise<readonly Campaign[]> =>
  (await apiGet<{ campaigns?: readonly Campaign[] }>('/consumer/penny-public/offers')).campaigns ?? [];
